import { useMemo } from 'react';
import { RecurrenceInterval } from '@sib/api';
import { useTasksAppTranslation } from '@sib/i18n';
import {
  allWeekdayCronTypes,
  OnTheWeekdayOrder,
  weekdayCronTypes,
} from './recurring-task-recurrence';

export const useIntervalOptions = () => {
  const { t } = useTasksAppTranslation();

  return useMemo(
    () => [
      {
        label: t('enums.RecurrenceInterval.day'),
        value: RecurrenceInterval.Day,
      },
      {
        label: t('enums.RecurrenceInterval.week'),
        value: RecurrenceInterval.Week,
      },
      {
        label: t('enums.RecurrenceInterval.month'),
        value: RecurrenceInterval.Month,
      },
    ],
    [t]
  );
};

interface UseRepeatEveryNumberOptionsProps {
  interval: RecurrenceInterval;
}

export const useRepeatEveryNumberOptions = (
  { interval }: UseRepeatEveryNumberOptionsProps = {
    interval: RecurrenceInterval.Day,
  }
) =>
  useMemo(() => {
    const count = {
      [RecurrenceInterval.Day]: 31,
      [RecurrenceInterval.Week]: 52,
      [RecurrenceInterval.Month]: 12,
    }[interval];

    return Array.from({ length: count }).map((_, index) => {
      const value = String(index + 1);

      return { label: value, value };
    });
  }, [interval]);

export const useWeekdaysOptions = () => {
  const { t } = useTasksAppTranslation();

  const options = useMemo(
    () =>
      weekdayCronTypes.map(day => ({
        value: day,
        label: t(`enums.recurringCronType.${day}`),
      })),
    [t]
  );

  const fullLengthOptions = useMemo(
    () =>
      allWeekdayCronTypes.map(day => ({
        value: day,
        label: t(`enums.recurringCronType.full.${day}`),
      })),
    [t]
  );

  return { options, fullLengthOptions };
};

export const useOnTheWeekdayOptions = () => {
  const { t } = useTasksAppTranslation();

  return useMemo(
    () => [
      {
        label: t('components.recurringTaskForm.fields.onThe.options.first'),
        value: OnTheWeekdayOrder.First,
      },
      {
        label: t('components.recurringTaskForm.fields.onThe.options.second'),
        value: OnTheWeekdayOrder.Second,
      },
      {
        label: t('components.recurringTaskForm.fields.onThe.options.third'),
        value: OnTheWeekdayOrder.Third,
      },
      {
        label: t('components.recurringTaskForm.fields.onThe.options.fourth'),
        value: OnTheWeekdayOrder.Fourth,
      },
      {
        label: t('components.recurringTaskForm.fields.onThe.options.last'),
        value: OnTheWeekdayOrder.Last,
      },
    ],
    [t]
  );
};
